/**
 * Which painted cockpit the pilot is looking at this frame, whether the guns
 * are flashing, and — new since the control-surface fidelity pass — which of
 * the three baked deflections the aileron/elevator/rudder art in that view
 * should show. The three are the pure decisions `CockpitPanel.tsx` makes
 * before it touches a canvas.
 *
 * ## Glancing swaps the panel, it does not just swing the camera
 *
 * The original's per-aircraft `.TTM` carries a *complete composition per view*
 * — forward, left, right and back, each its own run of draw blocks
 * (`packages/baker/src/cockpit-bake.ts`'s block table; the manual's own list is
 * "the forward, left, right, and back cockpit views",
 * `docs/game/manual/04-gameplay.md:673`). So turning your head repaints the
 * panel: dashboard forward, wing and struts to the side, turtledeck and
 * tailplane astern. Leaving the forward dashboard up while the camera yawed
 * was the bug behind the user's "Q/E tilts sideways irrespective of the
 * cockpit" (2026-08-19).
 *
 * `up` is the one glance with no art at all — it is a port addition, not one
 * of the original's four (`keys.ts`'s `LOOK_UP_KEY`), and the scripts have no
 * block group for it. The panel is hidden for it rather than invented.
 *
 * ## Control-surface deflection
 *
 * `packages/baker/src/cockpit-bake.ts` now bakes all three positions of every
 * surface the `.TTM`s draw (`CockpitComposition.controlSurfaces`); this module
 * is where a live stick/rudder value picks one of the three.
 * `controlSurfaceStateFrom` reads straight off the `FlightInputs` the flight
 * core itself consumes that tick (`flight-scene.ts`'s `onFrame` passes the
 * same `inputs` it hands to `runner.advance`) — never a second keyboard read,
 * so the painted surface can never disagree with what the aeroplane is
 * actually doing.
 *
 * Left and right glances key off *opposite* signs of the same roll value:
 * rolling right lowers the aileron on the wing that needs more lift (the
 * left one) and raises the one that needs less (the right one), so a single
 * `aileronDeflectionOf(inputs.roll)` feeds `leftAileron` directly and
 * `rightAileron` through `mirrorDeflection`. Elevator and rudder need no such
 * flip — `back`'s two axes are independent inputs (pitch, yaw), not the same
 * one seen from two sides.
 */

import type { CockpitComposition, CockpitDrawOp, ControlSurfaceDeflection } from "@red-opus/baker/schema";
import type { FlightInputs } from "@red-opus/engine";
import { dashFamilyOps } from "./cockpit-airframe.ts";
import type { GlanceDirection } from "./keys.ts";

/**
 * How far off centre a stick/rudder axis has to sit before the cockpit shows
 * a deflected surface instead of the neutral one. The `.TTM` art only has
 * three positions, so this is a simple deadzone rather than a blend; a
 * keyboard's digital ±1 clears it the instant a key is held, which is the
 * only input source this port has today (`keys.ts`).
 */
const DEFLECTION_DEADZONE = 0.35;

/** One `FlightInputs` axis value, quantized to the three positions `.TTM` art actually has. */
export function deflectionOf(axisValue: number): ControlSurfaceDeflection {
  if (axisValue > DEFLECTION_DEADZONE) return "positive";
  if (axisValue < -DEFLECTION_DEADZONE) return "negative";
  return "neutral";
}

/** `leftAileron`/`rightAileron` disagree by construction (differential ailerons) — see the module doc. */
function mirrorDeflection(deflection: ControlSurfaceDeflection): ControlSurfaceDeflection {
  if (deflection === "positive") return "negative";
  if (deflection === "negative") return "positive";
  return "neutral";
}

export interface ControlSurfaceState {
  /** Feeds `rightAileron` directly and `leftAileron` through `mirrorDeflection` — see the module doc. */
  readonly aileron: ControlSurfaceDeflection;
  readonly elevator: ControlSurfaceDeflection;
  readonly rudder: ControlSurfaceDeflection;
}

/** Every surface neutral — what a glance shows with the stick centred, and `cockpitFrameFor`/`cockpitFrameKey`'s default. */
export const NEUTRAL_SURFACES: ControlSurfaceState = { aileron: "neutral", elevator: "neutral", rudder: "neutral" };

/**
 * Reads the live control-surface state off the same `FlightInputs` the
 * flight core consumed this tick (`flight-scene.ts`'s `onFrame`) — see the
 * module doc for why this must never be a parallel keyboard read.
 */
export function controlSurfaceStateFrom(inputs: FlightInputs): ControlSurfaceState {
  return { aileron: deflectionOf(inputs.roll), elevator: deflectionOf(inputs.pitch), rudder: deflectionOf(inputs.yaw) };
}

/**
 * Which of the two cockpits the pilot is sitting in.
 *
 * `painted` is the original's: the whole composition for the glance, airframe
 * and all. `modelled` is 3D COCKPIT's (`effects-settings.ts`'s `cockpit3d`):
 * the aeroplane itself is drawn around the camera, so only the dashboard family
 * is painted and only in the forward view.
 */
export type CockpitPaintMode = "painted" | "modelled";

/** What to paint, or `null` to show no panel at all. */
export interface CockpitFrame {
  readonly ops: readonly CockpitDrawOp[];
  /**
   * Whether the `RB.EXE` instrument row goes on top. Only the forward view has
   * a dashboard to put it on: the glance blocks draw no dash sheet and paint
   * their own coaming straight across the band the gauges occupy, so a gauge
   * drawn in a glance would be a dial floating over the wing.
   */
  readonly showGauges: boolean;
}

/**
 * The composition for `glance`, with `surfaces` selecting which baked
 * deflection of the aileron (left/right glance) or elevator+rudder (back
 * glance) is showing. `firing` only affects the forward view — the
 * muzzle-flash blocks (5/6) belong to it alone, and from a side or rear glance
 * you cannot see the breeches anyway. `surfaces` defaults to neutral so every
 * existing caller (the `/assets` cockpit viewer, tests) still gets the static
 * picture without threading live inputs through.
 */
export function cockpitFrameFor(
  composition: CockpitComposition,
  glance: GlanceDirection,
  firing: boolean,
  surfaces: ControlSurfaceState = NEUTRAL_SURFACES,
  mode: CockpitPaintMode = "painted",
): CockpitFrame | null {
  // 3D COCKPIT: every glance but the forward one draws no overlay at all. The
  // side and rear compositions are *entirely* airframe — 96, 92 and 54 vector
  // ops apiece and not one sprite among them — so there is nothing in them to
  // keep once the real model is being drawn, and the classification the forward
  // view needs is a question they never ask.
  if (mode === "modelled") {
    if (glance !== "forward") return null;
    const ops = firing ? composition.firing : composition.idle;
    return { ops: dashFamilyOps(ops, composition.aircraft), showGauges: true };
  }
  switch (glance) {
    case "forward":
      return { ops: firing ? composition.firing : composition.idle, showGauges: true };
    case "left":
      return { ops: composition.controlSurfaces.leftAileron[mirrorDeflection(surfaces.aileron)], showGauges: false };
    case "right":
      return { ops: composition.controlSurfaces.rightAileron[surfaces.aileron], showGauges: false };
    case "back": {
      const back = composition.controlSurfaces.back;
      return back ? { ops: back[surfaces.elevator][surfaces.rudder], showGauges: false } : null;
    }
    default:
      return null;
  }
}

/**
 * A cache key that changes exactly when `cockpitFrameFor` would return
 * different ops, so the panel can keep one composed 320x200 canvas per view
 * instead of replaying ~100 draw ops every time the flash toggles or a
 * surface moves.
 */
export function cockpitFrameKey(
  glance: GlanceDirection,
  firing: boolean,
  surfaces: ControlSurfaceState = NEUTRAL_SURFACES,
  mode: CockpitPaintMode = "painted",
): string {
  // The mode is in the key, not only in the ops: the two modes paint *different
  // pictures for the same glance*, and a cache keyed without it would hand the
  // full painted forward view back to a pilot who has just switched the setting
  // on (and the dash-only one back when he switches it off).
  if (mode === "modelled") return glance === "forward" ? (firing ? "modelled-forward-firing" : "modelled-forward") : `modelled-${glance}`;
  switch (glance) {
    case "forward":
      return firing ? "forward-firing" : "forward";
    case "left":
      return `left-${mirrorDeflection(surfaces.aileron)}`;
    case "right":
      return `right-${surfaces.aileron}`;
    case "back":
      return `back-${surfaces.elevator}-${surfaces.rudder}`;
    default:
      return glance;
  }
}

/**
 * How many times a second the muzzle flash turns over.
 *
 * INFERRED. The original's cockpit `.TTM` carries the guns-idle blocks (3/4)
 * and the guns-firing blocks (5/6) as *separate frames* — a two-frame
 * animation, not a state — and `RB.EXE` alternates them as it redraws. At the
 * DOS build's frame rate (~20 fps in the captured sessions) alternating frames
 * is a ~10 Hz cycle, which is what this is. No live capture of a DOS burst
 * exists to time it against, so the number is reasoned rather than measured;
 * the *shape* — a flicker rather than a held fireball — is what the block
 * structure settles.
 */
export const MUZZLE_FLASH_HZ = 10;

/**
 * Whether the muzzle flash is painted this instant.
 *
 * The flash is an event per group of rounds, not a state. `guns.burstRounds` is
 * the engine's own count of rounds fired in the current burst, so counting
 * flashes off it ties the flicker to the rate of fire and makes it frame-rate
 * independent by construction — it reads a round count, not a clock. Holding
 * one static fireball for as long as the trigger was down is what the user
 * reported on 2026-08-19 ("the held gun fire animation fireball seems to be a
 * single static frame").
 *
 * At 55 rounds/s that is ~6 rounds per cycle: three lit, three dark.
 */
export function muzzleFlashVisible(burstRounds: number, jammed: boolean, roundsPerSecond: number): boolean {
  if (burstRounds <= 0 || jammed) return false;
  const perCycle = Math.max(2, Math.round(Math.max(roundsPerSecond, 1) / MUZZLE_FLASH_HZ));
  return Math.floor(burstRounds) % perCycle < perCycle / 2;
}
